import type { CoverImage, TemplateCategory, TemplateSubcategory } from "./api";

/**
 * Website template categories: four groups, each with three sub-categories
 * (mirrors internal/templates/taxonomy.go). Online stores are separate.
 * Photos are from Unsplash (free to use under the Unsplash License).
 */

export interface SubcategoryOption {
  slug: TemplateSubcategory;
  label: string;
  hint: string;
  cover: CoverImage;
}

export interface CategoryOption {
  slug: TemplateCategory;
  label: string;
  hint: string;
  cover: CoverImage;
  subcategories: SubcategoryOption[];
}

function unsplash(id: string, alt: string): CoverImage {
  return { url: `https://images.unsplash.com/photo-${id}`, alt };
}

export const CATEGORIES: CategoryOption[] = [
  {
    slug: "professionals",
    label: "Professionals",
    hint: "Practices, firms and consultancies",
    cover: unsplash("1521791136064-7986c2920216", "Two people shaking hands across a desk"),
    subcategories: [
      {
        slug: "legal",
        label: "Legal",
        hint: "Practice areas and consultations",
        cover: unsplash("1589829545856-d10d557cf95f", "Statue of Lady Justice holding scales"),
      },
      {
        slug: "finance-consulting",
        label: "Finance & Consulting",
        hint: "Services, case studies and pricing",
        cover: unsplash("1554224155-6726b3ff858f", "Financial reports and a calculator on a desk"),
      },
      {
        slug: "health-wellness",
        label: "Health & Wellness",
        hint: "Treatments and appointments",
        cover: unsplash("1544367567-0f2fcb009e0b", "Person in a yoga pose at sunset"),
      },
    ],
  },
  {
    slug: "artisans",
    label: "Artisans",
    hint: "Makers, crafts and kitchens",
    cover: unsplash("1611021061285-16c871740efa", "Hands carving wood with sawdust flying"),
    subcategories: [
      {
        slug: "fashion-tailoring",
        label: "Fashion & Tailoring",
        hint: "Lookbooks and made-to-measure",
        cover: unsplash("1594938298603-c8148c4dae35", "Man in a tailored blue check suit"),
      },
      {
        slug: "furniture-woodwork",
        label: "Furniture & Woodwork",
        hint: "Pieces, workshop and commissions",
        cover: unsplash("1530018607912-eff2daa1bac4", "Handmade wooden console table"),
      },
      {
        slug: "food-baking",
        label: "Food & Baking",
        hint: "Menus, orders and events",
        cover: unsplash("1509440159596-0249088772ff", "Loaves of rustic sourdough bread"),
      },
    ],
  },
  {
    slug: "developers",
    label: "Developers",
    hint: "Software, engineering and studios",
    cover: unsplash("1498050108023-c5249f4df085", "Laptop with code on a bright desk"),
    subcategories: [
      {
        slug: "software-web",
        label: "Software & Web",
        hint: "Projects, stack and open source",
        cover: unsplash("1461749280684-dccba630e2f6", "Code on a dark screen"),
      },
      {
        slug: "engineering",
        label: "Engineering",
        hint: "Projects and experience",
        cover: unsplash("1581092160562-40aa08e78837", "Engineer reviewing mechanical drawings"),
      },
      {
        slug: "tech-studios",
        label: "Tech Studios & Agencies",
        hint: "Services, client work and team",
        cover: unsplash("1522071820081-009f0129c71c", "Team working on laptops around a table"),
      },
    ],
  },
  {
    slug: "creatives",
    label: "Creatives",
    hint: "Photography, design and content",
    cover: unsplash("1513364776144-60967b0f800f", "Paint brushes and bright paint"),
    subcategories: [
      {
        slug: "photography-film",
        label: "Photography & Film",
        hint: "Galleries, packages and booking",
        cover: unsplash("1452587925148-ce544e77e70d", "Vintage camera and travel photos on a map"),
      },
      {
        slug: "design-architecture",
        label: "Design & Architecture",
        hint: "Case studies and process",
        cover: unsplash("1487958449943-2429e8be8625", "Angular white building facade"),
      },
      {
        slug: "writers-music-content",
        label: "Writers, Music & Content",
        hint: "Work, press and audience",
        cover: unsplash("1511379938547-c1f69419868d", "Guitars and a piano in a home studio"),
      },
    ],
  },
];

/** Cover for "every template" and for sites without a known template. */
export const ALL_TEMPLATES_COVER: CoverImage = unsplash(
  "1497366811353-6870744d04b2",
  "Sunlit loft meeting room",
);

export function findCategory(slug: string | null | undefined): CategoryOption | undefined {
  return CATEGORIES.find((category) => category.slug === slug);
}

export function findSubcategory(slug: string | null | undefined): SubcategoryOption | undefined {
  for (const category of CATEGORIES) {
    const sub = category.subcategories.find((option) => option.slug === slug);
    if (sub) return sub;
  }
  return undefined;
}

/** The category a sub-category belongs to. */
export function categoryOf(subcategory: string | null | undefined): CategoryOption | undefined {
  return CATEGORIES.find((category) => category.subcategories.some((sub) => sub.slug === subcategory));
}

export function categoryLabel(slug: string | null | undefined): string {
  return findCategory(slug)?.label ?? "";
}

export function subcategoryLabel(slug: string | null | undefined): string {
  return findSubcategory(slug)?.label ?? "";
}
